import React from "react";
import { Button, Card, EmptyState, ListItemCard, Skeleton, maskApiKey } from "../../../ui";

interface ApiKeysViewProps {
  apiKeys: string[];
  isLoading?: boolean;
  isGenerating: boolean;
  onGenerate: () => void;
  onCopy: (key: string) => void;
  onDelete: (key: string) => void;
}

export function ApiKeysView({
  apiKeys,
  isLoading = false,
  isGenerating,
  onGenerate,
  onCopy,
  onDelete,
}: ApiKeysViewProps) {
  return (
    <Card className="p-5">
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Use API keys to authenticate requests to the Composo evaluation API.
        </p>
        <Button onClick={onGenerate} disabled={isGenerating || isLoading}>
          {isGenerating ? "Generating..." : "Generate New API Key"}
        </Button>
      </div>

      <div className="space-y-2">
        {/* Skeleton a key row until the fetch resolves, so we never flash the
            "No API keys" empty state before the user's keys load (review v2). */}
        {isLoading && (
          <ListItemCard>
            <Skeleton variant="text" height={16} width={220} />
            <div className="flex gap-2">
              <Skeleton height={32} width={64} className="rounded-md" />
              <Skeleton height={32} width={72} className="rounded-md" />
            </div>
          </ListItemCard>
        )}

        {!isLoading && apiKeys.length === 0 && (
          <EmptyState message="No API keys have been generated yet." />
        )}

        {apiKeys.map((key, index) => (
          <ListItemCard key={index}>
            <code className="font-mono text-sm text-foreground">{maskApiKey(key)}</code>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onCopy(key)}>
                Copy
              </Button>
              <Button
                variant="dangerOutline"
                size="sm"
                onClick={() => {
                  if (
                    window.confirm(
                      "Are you sure you want to delete this API key? This action cannot be undone."
                    )
                  ) {
                    onDelete(key);
                  }
                }}
              >
                Delete
              </Button>
            </div>
          </ListItemCard>
        ))}
      </div>
    </Card>
  );
}
